import { mailTypeLabel } from "@shared/mail-rules.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Card, Chip, EmptyState, FilterGroup } from "../components/account-ui.tsx";
import { Modal, useToast } from "../components/legacy/ui.tsx";
import { StartCampaignModal } from "../components/StartCampaignModal.tsx";
import { CampaignOutlineIcon, ShoppingBagIcon } from "../icons/index.tsx";
import { api, type Campaign, type PriceTierRow, type Product } from "../lib/api.ts";
import type { CampaignStart } from "../lib/campaign-start.ts";
import {
  basePriceCents,
  type CatalogSearch,
  facetValues,
  filterProducts,
  hasActiveFilters,
  type MailFilter,
  parseCatalogSearch,
} from "../lib/catalog.ts";
import { formatPrices } from "../lib/legacy.ts";
import { useAppConfig } from "./__root.tsx";

export const Route = createFileRoute("/")({
  component: Catalog,
  validateSearch: parseCatalogSearch,
});

const MAIL: { id: MailFilter | "all"; label: string }[] = [
  { id: "all", label: "All" },
  { id: "unaddressed", label: "Unaddressed" },
  { id: "addressed", label: "Addressed" },
];

/**
 * The storefront. Every product is shown, and the filters live in the URL so
 * a link to a shortlist can be shared and the back button undoes a change.
 * Starting a campaign is the same action the collection page used to offer.
 */
function Catalog() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const { data: config } = useAppConfig();
  const user = config?.user ?? null;
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [starting, setStarting] = useState<Product | null>(null);

  const { data } = useQuery({
    queryKey: ["catalog"],
    queryFn: () => api.get<{ products: Product[]; tiers: PriceTierRow[] }>("/catalog/products"),
  });

  const create = useMutation({
    mutationFn: (body: CampaignStart) =>
      api.post<{ campaign: Campaign }>("/campaigns", { name: "New Campaign", ...body }),
    onSuccess: ({ campaign }) => {
      setStarting(null);
      void queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      navigate({
        to: "/app/$customerId/mycampaigns/$campaignId",
        params: { customerId: user!.id, campaignId: campaign.id },
      });
    },
    onError: (e: Error) => showToast(e.message, "error"),
  });

  const products = data?.products ?? [];
  const tiers = data?.tiers ?? [];
  const shown = filterProducts(products, tiers, search);
  const facets = facetValues(products);
  const filtering = hasActiveFilters(search);

  const apply = (patch: Partial<CatalogSearch>, replace = false) => {
    const next: CatalogSearch = { ...search, ...patch };
    for (const key of Object.keys(next) as (keyof CatalogSearch)[]) {
      if (next[key] === undefined) delete next[key];
    }
    navigate({ to: "/", search: next, replace });
  };

  // A return from sign-in lands on /?start=<id>. Open that product, then drop
  // the param so refresh and Close Campaign see a plain catalog.
  // biome-ignore lint/correctness/useExhaustiveDependencies: one-shot resume; apply reads the search from this render
  useEffect(() => {
    if (!search.start || !data) return;
    setStarting(data.products.find((p) => p.id === search.start) ?? null);
    apply({ start: undefined }, true);
  }, [search.start, data]);

  const choose = <K extends keyof CatalogSearch>(key: K, value: CatalogSearch[K]) =>
    apply({ [key]: search[key] === value ? undefined : value });

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <Card className="z-[5] mb-6 lg:sticky lg:top-[calc(var(--header-h)+1rem)]">
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
              <FilterGroup label="Mail type">
                <div className="inline-flex rounded-xl bg-gray-100 p-1">
                  {MAIL.map((m) => {
                    const active = (search.mail ?? "all") === m.id;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        aria-pressed={active}
                        className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                          active
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                        onClick={() => apply({ mail: m.id === "all" ? undefined : m.id })}
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              </FilterGroup>

              {facets.sizes.length > 0 && (
                <FilterGroup label="Size">
                  {facets.sizes.map((size) => (
                    <Chip
                      key={size}
                      active={search.size === size}
                      onClick={() => choose("size", size)}
                    >
                      {size}
                    </Chip>
                  ))}
                </FilterGroup>
              )}

              {facets.pages.length > 1 && (
                <FilterGroup label="Pages">
                  {facets.pages.map((pages) => (
                    <Chip
                      key={pages}
                      active={search.pages === pages}
                      onClick={() => choose("pages", pages)}
                    >
                      {`${pages}pp`}
                    </Chip>
                  ))}
                </FilterGroup>
              )}

              {filtering && (
                <button
                  type="button"
                  className="text-sm font-medium text-primary-600 hover:text-primary-500"
                  onClick={() => navigate({ to: "/", search: {} })}
                >
                  Reset
                </button>
              )}

              <label className="ml-auto flex items-center gap-2 text-sm text-gray-600">
                Sort
                <select
                  className="rounded-lg border-gray-300 py-1.5 pl-3 pr-8 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500"
                  value={search.sort ?? "recommended"}
                  onChange={(e) =>
                    apply({
                      sort:
                        e.target.value === "recommended"
                          ? undefined
                          : (e.target.value as CatalogSearch["sort"]),
                    })
                  }
                >
                  <option value="recommended">Recommended</option>
                  <option value="price-asc">Price, low to high</option>
                  <option value="price-desc">Price, high to low</option>
                </select>
              </label>
            </div>
          </div>
        </Card>

        {data && shown.length === 0 ? (
          <Card bodyClass="">
            <EmptyState
              Icon={ShoppingBagIcon}
              title="No products match"
              message="Try a wider set of filters to see more formats."
              action={
                <button
                  type="button"
                  className="rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700"
                  onClick={() => navigate({ to: "/", search: {} })}
                >
                  Clear Filters
                </button>
              }
            />
          </Card>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {shown.map((p) => (
              <button
                key={p.id}
                type="button"
                disabled={create.isPending}
                className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white text-left shadow-sm transition-shadow hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => setStarting(p)}
              >
                <div className="flex aspect-[4/3] items-center justify-center bg-gray-50 p-6">
                  {p.image && (
                    <img src={p.image} alt="" className="max-h-full max-w-full object-contain" />
                  )}
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex flex-wrap gap-1.5">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                        p.addressed_mail === 1 && p.unaddressed_mail === 1
                          ? "bg-gray-100 text-gray-700"
                          : p.addressed_mail
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-sky-100 text-sky-800"
                      }`}
                    >
                      {mailTypeLabel(p)}
                    </span>
                    {p.size && (
                      <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-700">
                        {p.size}
                      </span>
                    )}
                  </div>
                  <p className="mt-3 text-sm font-semibold text-gray-900">{p.name}</p>
                  {p.description && (
                    <p className="mt-1 line-clamp-2 text-sm text-gray-500">{p.description}</p>
                  )}
                  <p className="mt-2 text-xs text-gray-500">{specLine(p)}</p>
                  <p className="mt-3 text-sm font-semibold tabular-nums text-gray-900">
                    from ${formatPrices(basePriceCents(tiers, p.id) / 100)} each
                  </p>
                  <span className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors group-hover:bg-primary-700">
                    <CampaignOutlineIcon className="h-5 w-5" />
                    Start Campaign
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {user && (
        <StartCampaignModal
          product={starting}
          user={user}
          pending={create.isPending}
          onClose={() => setStarting(null)}
          onStart={(body) => create.mutate(body)}
        />
      )}

      <Modal
        open={!user && starting !== null}
        title="Sign in to create your campaign"
        onClose={() => setStarting(null)}
        onSubmit={() => {
          if (!starting) return;
          const redirect = router.buildLocation({
            to: "/",
            search: { ...search, start: starting.id },
          }).href;
          navigate({ to: "/sign-in", search: { redirect } });
        }}
        submitText="Sign In"
      >
        <div>
          To proceed with campaign creation and place your order, please sign in to your account.
          This ensures a seamless experience and allows you to:
          <ul className="mt-3 ml-4 list-disc text-sm">
            <li>Create and manage personalized campaigns</li>
            <li>Track your orders &amp; many more.</li>
          </ul>
        </div>
      </Modal>
    </div>
  );
}

function specLine(p: Product): string {
  const stock = [p.gsm ? `${p.gsm}gsm` : null, p.stock].filter(Boolean).join(" ");
  return [`${p.width_mm} x ${p.height_mm}mm`, `${p.pages}pp`, stock].filter(Boolean).join(" · ");
}
